import { CalcProductLogic } from "@biz/items/CalcProductLogic";
import { CalcCurtainLogic } from "@biz/items/curtain/CalcCurtainLogic";
import { ICurtainData } from "@biz/items/curtain/CurtainDefinitions";
import { CurtainLogic } from "@biz/items/curtain/CurtainLogic";
import { FieldErrorMap } from "@biz/models/FieldErrorMap";
import { IShoppingArticleOptions } from "@biz/models/ShoppingListDefinitions";
import { PD } from "@biz/PD";
import { ICalcContext, ICalculatedProductResult, ProjectCheckErrorType } from "@biz/projectCalculation/ProjectDefinitions";

export class CalcCurtain3dLogic {
  static calc3d(curtain: ICurtainData, slc: ICalcContext): ICalculatedProductResult {
    switch (curtain.curtainType) {
      case "guide": {
        CalcCurtain3dLogic.#calcGuides(slc, curtain);
        break;
      }
      case "rope": {
        CalcCurtain3dLogic.#calcRope(slc, curtain);
        break;
      }
    }

    CalcCurtain3dLogic.#calcSuspensionTypes(slc, curtain);

    CalcCurtain3dLogic.#calcAdditionalShoppingCart(slc, curtain);

    CalcCurtain3dLogic.#calcClothes(slc, curtain);

    CalcProductLogic.finalizeCalcContext(slc, { incomplete: !curtain, defaultOfferList: "H", count: curtain.count });

    CalcCurtainLogic.calcCurtainErrors(slc);

    return {
      id: curtain.id,
      pd: PD.productToPD(curtain),
      infos: slc.infos,
      errors: slc.errors,
      errorMap: FieldErrorMap.errorMapFromErrors(slc.errors || []),
      articles: slc.ruleArticles,
      // modified: curtain,
      product: curtain
      // modifiedSunSail: sunSail,
    };
  }

  static #calcGuides(slc: ICalcContext, curtain: ICurtainData): void {
    for (let iTrack = 0; iTrack < curtain.tracks.length; iTrack++) {
      const guides = CurtainLogic.calcTrackGuides(curtain, iTrack)
      if (guides.length > 0) {
        CalcProductLogic.addRuleArticle(slc, "00E13001214", { count: guides.length - 1, articleType: "obligatory", articleGroup: "curtainSlide" }); // Verbindungsschiene

        for (const g of guides) {
          CalcCurtainLogic.addGuideInfoArticles(slc, g);
        }
      }
    }
  }

  static #calcRope(slc: ICalcContext, curtain: ICurtainData): void {
    const optionsRope: Partial<IShoppingArticleOptions> = {
      idCategory: "additional",
      partList: { group: "Warenkorb" },
      articleGroup: "curtainRope"
    }

    for (let iTrack = 0; iTrack < curtain.tracks.length; iTrack++) {
      for (let iCorner = 0; iCorner < curtain.tracks[iTrack].corners.length; iCorner++) {
        // const corner = curtain.tracks[iTrack].corners[iCorner];
        const ropeMount = CurtainLogic.getCornerArticle(curtain, iTrack, iCorner, "ropeMount");
        if (ropeMount) {
          CalcProductLogic.addRuleArticle(slc, ropeMount, { count: 1, articleType: "obligatory", ...optionsRope }); // 00E13000255?
        }
      }
    }

    const optionsRopeMount: Partial<IShoppingArticleOptions> = {
      idCategory: "additional",
      partList: { group: "Warenkorb" },
      articleGroup: "curtainRopeMount"
    }
    for (let iTrack = 0; iTrack < curtain.tracks.length; iTrack++) {
      const ropes = CurtainLogic.getRopeParts(curtain, iTrack);
      for (const rope of ropes) {
        CalcCurtainLogic.addCurtainRope(slc, rope, { ...optionsRopeMount })
      }

      const track = curtain.tracks[iTrack];
      for (let iCorner = 0; iCorner < track.corners.length; iCorner++) {
        CalcProductLogic.addRuleArticle(slc, CurtainLogic.getCornerArticle(curtain, iTrack, iCorner, "startTensioner"), { count: 1, articleType: "obligatory", ...optionsRopeMount });
        CalcProductLogic.addRuleArticle(slc, CurtainLogic.getCornerArticle(curtain, iTrack, iCorner, "endTensioner"), { count: 1, articleType: "obligatory", ...optionsRopeMount });
      }
    }
  }

  static #calcSuspensionTypes(slc: ICalcContext, curtain: ICurtainData): void {
    for (let iTrack = 0; iTrack < curtain.tracks.length; iTrack++) {
      for (let iCloth = 0; iCloth < curtain.tracks[iTrack].clothes.length; iCloth++) {
        const suspensionType = CurtainLogic.getClothSuspensionType(curtain, iTrack, iCloth);
        const fixCount = CurtainLogic.calcClothFixCount(curtain, iTrack, iCloth);
        switch (suspensionType) {
          case "glider": {
            CalcProductLogic.addRuleArticle(slc, "00E13001774", { count: fixCount, articleType: "obligatory", articleGroup: "curtainSuspension", });
            break;
          }
          case "plasticRoll": {
            CalcProductLogic.addRuleArticle(slc, "00E13001300", { count: fixCount, articleType: "obligatory", articleGroup: "curtainSuspension", });
            break;
          }
          case "metalRoll": {
            CalcProductLogic.addRuleArticle(slc, "00E13001715", { count: fixCount, articleType: "obligatory", articleGroup: "curtainSuspension", });
            break;
          }
          case "carabiner":
            CalcProductLogic.addRuleArticle(slc, "00E13001224", { count: fixCount, articleType: "obligatory", articleGroup: "curtainSuspension", });
            break;

          case "carabinerPulley":
            // CalcSailLogic.addRuleArticle(slc, "00E13001224", { count: fixCount, articleType: "obligatory", articleGroup: "curtainSuspension" });
            // CalcSailLogic.addRuleArticle(slc, "00E13000222", { count: fixCount, articleType: "obligatory", articleGroup: "curtainSuspension" });
            CalcProductLogic.addRuleArticle(slc, "00E13001952", { count: fixCount, articleType: "obligatory", articleGroup: "curtainSuspension" });
            break;
        }
      }
    }
  }

  static #calcClothes(slc: ICalcContext, curtain: ICurtainData): void {

    const clothInfos = CurtainLogic.calcClothInfos(curtain);
    CalcCurtainLogic.calcClothArticlesFromClothInfos(slc, clothInfos);

    for (let iTrack = 0; iTrack < curtain.tracks.length; iTrack++) {
      const trackLength = CurtainLogic.getTrackLength(curtain, iTrack);
      const segments = CurtainLogic.calcClothSegments(curtain, iTrack).map((s, index) => ({ ...s, index }));
      const sorted = segments.sort((a, b) => a.from - b.from);
      for (let i = 0; i < sorted.length; i++) {
        const current = sorted[i];
        if (current.from < 0)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Hint, `Cloth_${iTrack + 1}_${current.index + 1}`, slc.lc.f("Rule_Curtain_Outside", i + 1));
        if (current.to > trackLength)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Hint, `Cloth_${iTrack + 1}_${current.index + 1}`, slc.lc.f("Rule_Curtain_Outside", i + 1));
        for (let j = i + 1; j < sorted.length; j++) {
          const other = sorted[j];
          if (other.from < current.to) {
            CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Hint, `Cloth_${iTrack + 1}_${current.index + 1}`, slc.lc.f("Rule_Curtain_Collides", [i + 1, j + 1]));
          }
        }
      }
    }
  }

  static #calcAdditionalShoppingCart(slc: ICalcContext, curtain: ICurtainData): void {
    const options: Partial<IShoppingArticleOptions> = {
      idCategory: "additional",
      partList: { group: "Warenkorb" },
      articleGroup: "curtainAdditional"
    }
    CalcCurtainLogic.addCurtainMountArticles(slc, curtain.curtainType, "3d", options);

    CalcCurtainLogic.addCurtainClothAdditionalArticles(slc, curtain.curtainType, "3d", { count: 0, articleType: "optional", quickCategory: "curtainAdditional", ...options })
  }
}